import {
  getBulkUsersByUuid,
  getUserByUuidOrEmail,
  type BulkDtoUserApiDto,
  type UserApiDto,
  type UserIdentityDto,
  type ApiError,
} from './generated/index';
import type { Client } from '@hey-api/client-fetch';
import { useQuery, type UseQueryResult } from '@tanstack/solid-query';
import type { Accessor } from 'solid-js';

const USER_STALE_TIME = 300_000; // 5 minutes
const BULK_USER_CHUNK_SIZE = 25;

type UserNameFields = Pick<UserIdentityDto, 'firstName' | 'lastName' | 'email'>;

/**
 * Extracts the UUID segment from a `trn:tid:users:<uuid>` string.
 */
export function parseUuidFromTrn(value: string): string {
  const parts = value.split(':');
  return parts[parts.length - 1];
}

/**
 * Collects unique tiduuids for `/users/tid/bulk` from mission creator TRNs/UUIDs.
 */
export function collectBulkUserUuids(userTrns: readonly string[]): string[] {
  return [
    ...new Set(
      userTrns
        .map((userTrn) => parseUuidFromTrn(userTrn.trim()))
        .filter((uuid) => uuid.length > 0)
    ),
  ].sort((a, b) => a.localeCompare(b));
}

function chunkArray<T>(items: readonly T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
}

export class TcUserApiService {
  constructor(private readonly client: Client) {}

  public getUserByIdQuery(
    userId: Accessor<string | null | undefined>
  ): UseQueryResult<UserIdentityDto, Error> {
    return useQuery(() => ({
      queryKey: ['tc-user', 'uuid', userId()],
      enabled: Boolean(userId()?.trim()),
      staleTime: USER_STALE_TIME,
      queryFn: async (): Promise<UserIdentityDto> => {
        const uuid = parseUuidFromTrn(userId()!);
        const response = await getUserByUuidOrEmail({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generated client types don't align perfectly with @hey-api/client-fetch Client
          client: this.client as any,
          query: { uuid },
        });
        if (response.error) {
          throw new Error('Failed to resolve user', { cause: response.error });
        }
        return response.data!;
      },
    }));
  }

  public getBulkUsersByUuidQuery(
    userTrns: Accessor<readonly string[]>
  ): UseQueryResult<BulkDtoUserApiDto, Error> {
    return useQuery(() => {
      const uuids = collectBulkUserUuids(userTrns());

      return {
        queryKey: ['tc-user', 'bulk', ...uuids],
        enabled: uuids.length > 0,
        staleTime: USER_STALE_TIME,
        placeholderData: (previousData) => previousData,
        queryFn: (): Promise<BulkDtoUserApiDto> => {
          return fetchBulkUsersByUuids(this.client, uuids);
        },
      };
    });
  }
}

export async function fetchBulkUsersByUuids(
  client: Client,
  uuids: readonly string[]
): Promise<BulkDtoUserApiDto> {
  const chunks = chunkArray(uuids, BULK_USER_CHUNK_SIZE);
  const mergedItems: UserApiDto[] = [];
  const mergedErrors: NonNullable<BulkDtoUserApiDto['errors']> = [];
  let failedChunkCount = 0;

  for (const chunk of chunks) {
    const response = await getBulkUsersByUuid({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generated client types don't align perfectly with @hey-api/client-fetch Client
      client: client as any,
      body: {
        items: chunk.map((tiduuid) => ({ tiduuid })),
      },
    });

    if (response.error) {
      failedChunkCount += 1;
      // eslint-disable-next-line no-console -- We need visibility for support/debugging when chunk-level bulk lookup fails
      console.error('Bulk user lookup chunk failed', {
        chunkSize: chunk.length,
        chunkIdentifiers: chunk,
        error: response.error,
      });
      mergedErrors.push(
        ...chunk.map((tiduuid) => ({
          id: tiduuid,
          details: response.error as ApiError,
        }))
      );
      continue;
    }

    mergedItems.push(...(response.data?.items ?? []));
    mergedErrors.push(...(response.data?.errors ?? []));
  }

  if (failedChunkCount > 0 && mergedItems.length === 0) {
    throw new Error(
      `Failed to resolve any users from bulk lookup (${failedChunkCount}/${chunks.length} chunks failed)`
    );
  }

  return {
    items: mergedItems,
    errors: mergedErrors.length > 0 ? mergedErrors : undefined,
  };
}

export function formatUserDisplayName(
  user: UserNameFields | undefined
): string {
  if (!user) return '';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email || '';
}

function addLookupKey(
  lookup: Map<string, string>,
  key: string | undefined,
  displayName: string
): void {
  if (!key?.trim()) {
    return;
  }
  lookup.set(key.trim(), displayName);
}

export function buildUserDisplayNameByTrnLookup(
  response: BulkDtoUserApiDto | undefined,
  requestedIdentifiers: readonly string[] = []
): ReadonlyMap<string, string> {
  const lookup = new Map<string, string>();

  for (const user of response?.items ?? []) {
    const displayName = formatUserDisplayName(user);
    if (!displayName) {
      continue;
    }

    addLookupKey(lookup, user.trn, displayName);
    addLookupKey(lookup, user.id, displayName);
    addLookupKey(lookup, user.tiduuid, displayName);

    if (user.tiduuid) {
      addLookupKey(lookup, `trn:tid:users:${user.tiduuid}`, displayName);
    }
  }

  for (const requestedIdentifier of requestedIdentifiers) {
    const trimmed = requestedIdentifier.trim();
    if (!trimmed || lookup.has(trimmed)) {
      continue;
    }

    const uuid = parseUuidFromTrn(trimmed);
    const resolved =
      lookup.get(trimmed) ??
      lookup.get(uuid) ??
      [...lookup.entries()].find(
        ([key]) => parseUuidFromTrn(key) === uuid
      )?.[1];

    if (resolved) {
      lookup.set(trimmed, resolved);
    }
  }

  return lookup;
}

export function resolveUserDisplayNameFromLookup(
  lookup: ReadonlyMap<string, string>,
  userTrn: string | undefined
): string {
  if (!userTrn?.trim()) {
    return '';
  }

  const trimmed = userTrn.trim();
  return lookup.get(trimmed) ?? lookup.get(parseUuidFromTrn(trimmed)) ?? '';
}
